import type {
  ReservationSlotTargetShift,
  ReservationSlotSummary,
  ReservationSlotGenerationResult,
  ReservationSlotTemplate,
  CategoryCounts,
  TreatmentCategory,
} from '../../../domain/reservationSlots';
import type {
  ReservationSlotRepository,
  GenerateReservationSlotsInput,
} from '../../../application/reservationSlots/ports/ReservationSlotRepository';
import { reservationSlotTemplateRepositoryMock } from './ReservationSlotTemplateRepositoryMock';
import { mockDelay } from './mockUtils';

/**
 * サンプル医師（対象シフトはバックエンドが医師テーブルと結合して返すため、モック内で名前を持つ）
 */
const SAMPLE_DOCTORS = [
  { id: 1001, name: '武末 先生' },
  { id: 1002, name: '佐藤 先生' },
  { id: 1003, name: '鈴木 先生' },
];

/** サンプル勤務日（各月の固定日） */
const SAMPLE_DAYS = [5, 12, 20];

/** サンプル勤務時間（医師インデックスで割り当て） */
const SAMPLE_TIMES = [
  { start: '10:00', end: '13:00' },
  { start: '14:00', end: '18:00' },
  { start: '09:00', end: '12:00' },
];

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

/** "HH:MM" を分に変換する */
function toMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

/**
 * テンプレートと勤務時間から、生成される解放枠のカテゴリ別枠数を計算する（要件定義 §6 準拠）
 */
function computeSlotCounts(
  template: ReservationSlotTemplate,
  startTime: string,
  endTime: string
): CategoryCounts {
  const counts: CategoryCounts = {};
  const startMin = toMinutes(startTime);
  const endMin = toMinutes(endTime);
  const interval = template.intervalMinutes;
  const intervalCount = Math.floor((endMin - startMin) / interval);

  for (let i = 0; i < intervalCount; i += 1) {
    const clockMin = (startMin + i * interval) % 60;
    let slots: TreatmentCategory[];
    if (template.patternType === 'single') {
      slots = template.slotsA;
    } else if (interval === 15) {
      slots = clockMin === 0 || clockMin === 30 ? template.slotsA : template.slotsB ?? template.slotsA;
    } else {
      slots = clockMin === 0 ? template.slotsA : template.slotsB ?? template.slotsA;
    }
    for (const category of slots) {
      counts[category] = (counts[category] ?? 0) + 1;
    }
  }
  return counts;
}

function monthKey(year: number, month: number): string {
  return `${year}-${pad2(month)}`;
}

/**
 * 解放枠 Repository のモック実装
 *
 * @description
 * 対象シフト（サンプル）、解放枠生成、月別サマリをメモリ上で扱う。
 * 生成状態は月ごとに保持し、findTargetShifts の sourceTemplateId に反映する。
 *
 * @implements {ReservationSlotRepository}
 */
export class ReservationSlotRepositoryMock implements ReservationSlotRepository {
  /** 月キー → { doctorShiftId: templateId } の生成済み割り当て */
  private generatedByMonth: Map<string, Record<number, string>> = new Map();
  private summaries: ReservationSlotSummary[] = [];

  constructor() {
    // 一覧タブの初期表示用に、前月のサンプルサマリを1件シードする
    const now = new Date();
    const currentMonth = now.getMonth() + 1;
    const currentYear = now.getFullYear();
    const prevMonth = currentMonth === 1 ? 12 : currentMonth - 1;
    const prevYear = currentMonth === 1 ? currentYear - 1 : currentYear;
    this.summaries.push({
      year: prevYear,
      month: prevMonth,
      totalSlots: 108,
      categoryCounts: { pill: 84, skin: 12, wellness_clinic: 12 },
    });
  }

  /** 指定月の対象シフトを決定的に生成する */
  private buildShifts(year: number, month: number): ReservationSlotTargetShift[] {
    const generated = this.generatedByMonth.get(monthKey(year, month)) ?? {};
    const shifts: ReservationSlotTargetShift[] = [];
    let idx = 0;
    for (const day of SAMPLE_DAYS) {
      SAMPLE_DOCTORS.forEach((doctor, doctorIndex) => {
        const doctorShiftId = year * 1000000 + month * 10000 + idx;
        const time = SAMPLE_TIMES[doctorIndex % SAMPLE_TIMES.length];
        shifts.push({
          doctorShiftId,
          date: `${year}-${pad2(month)}-${pad2(day)}`,
          startTime: time.start,
          endTime: time.end,
          doctorId: doctor.id,
          doctorName: doctor.name,
          sourceTemplateId: generated[doctorShiftId] ?? null,
        });
        idx += 1;
      });
    }
    return shifts;
  }

  async findTargetShifts(year: number, month: number): Promise<ReservationSlotTargetShift[]> {
    await mockDelay();
    return this.buildShifts(year, month);
  }

  async generate(input: GenerateReservationSlotsInput): Promise<ReservationSlotGenerationResult> {
    await mockDelay(600);
    const shifts = this.buildShifts(input.year, input.month);
    const shiftMap = new Map(shifts.map((s) => [s.doctorShiftId, s]));

    const categoryCounts: CategoryCounts = {};
    const generatedAssignments: Record<number, string> = {};

    for (const assignment of input.assignments) {
      const template = reservationSlotTemplateRepositoryMock.findByIdSync(assignment.templateId);
      const shift = shiftMap.get(assignment.doctorShiftId);
      if (!template || !shift) {
        continue;
      }
      const counts = computeSlotCounts(template, shift.startTime, shift.endTime);
      for (const [category, count] of Object.entries(counts)) {
        const key = category as TreatmentCategory;
        categoryCounts[key] = (categoryCounts[key] ?? 0) + (count ?? 0);
      }
      generatedAssignments[assignment.doctorShiftId] = assignment.templateId;
    }

    // 月の生成状態を洗い替え（要件定義 §7）
    this.generatedByMonth.set(monthKey(input.year, input.month), generatedAssignments);

    const createdCount = Object.values(categoryCounts).reduce((sum, n) => sum + (n ?? 0), 0);
    this.upsertSummary({
      year: input.year,
      month: input.month,
      totalSlots: createdCount,
      categoryCounts,
    });

    return { createdCount, categoryCounts };
  }

  async findSummaries(): Promise<ReservationSlotSummary[]> {
    await mockDelay();
    return [...this.summaries].sort((a, b) => {
      if (a.year !== b.year) {
        return b.year - a.year;
      }
      return b.month - a.month;
    });
  }

  /** 同一年月のサマリを差し替え、なければ追加する */
  private upsertSummary(summary: ReservationSlotSummary): void {
    const index = this.summaries.findIndex(
      (s) => s.year === summary.year && s.month === summary.month
    );
    if (index === -1) {
      this.summaries.push(summary);
    } else {
      this.summaries[index] = summary;
    }
  }
}

export const reservationSlotRepositoryMock = new ReservationSlotRepositoryMock();
